import React, { useState } from 'react';
import { TextField, IconButton } from '@mui/material';
import { mostrarInfo } from '../../utils/Alertas';
import Modal from '../secondary/Modal';
import './css/CalculadoraRaciones.css';

interface Persona {
	nombre: string;
	racion: number;
}

const CalculadoraRaciones: React.FC = () => {
	const [pesoTotal, setPesoTotal] = useState<number | ''>('');
	const [personas, setPersonas] = useState<Persona[]>([
		{ nombre: 'Paula', racion: 0 },
		{ nombre: 'Alfonso', racion: 0 },
	]);
	const [resultados, setResultados] = useState<{ nombre: string; gramos: number }[]>([]);
	const [mostrarModalResultado, setMostrarModalResultado] = useState(false);

	const actualizarPersona = (index: number, campo: keyof Persona, valor: string | number) => {
		setPersonas((prev) =>
			prev.map((p, i) => (i === index ? { ...p, [campo]: valor } : p))
		);
	};

	const agregarPersona = () => {
		setPersonas((prev) => [...prev, { nombre: `Persona ${prev.length + 1}`, racion: 0 }]);
	};

	const eliminarPersona = (index: number) => {
		setPersonas((prev) => prev.filter((_, i) => i !== index));
	};

	const calcular = () => {
        if (!pesoTotal || pesoTotal <= 0) {
            return mostrarInfo('Introduce el peso total cocinado.');
        }

        const validas = personas.filter((p) => p.racion > 0);

        if (validas.length === 0) {
            return mostrarInfo('Añade al menos una persona con ración mayor que 0.');
        }

        const sumaRaciones = validas.reduce((s, p) => s + p.racion, 0);

        const exactos = validas.map((p) => ({
            nombre: p.nombre || 'Sin nombre',
            exacto: (p.racion / sumaRaciones) * (pesoTotal as number),
        }));

        const conMitad = exactos.filter(
            (r) => Math.abs(r.exacto % 1 - 0.5) < 0.0001
        );

        let menorMitad: string | null = null;

        if (conMitad.length > 1) {
            menorMitad = [...conMitad].sort((a, b) => a.exacto - b.exacto)[0].nombre;
        }

        const res = exactos.map((r) => {
            let gramos: number;

            const decimal = r.exacto % 1;

            if (Math.abs(decimal - 0.5) < 0.0001) {
                gramos = r.nombre === menorMitad
                    ? Math.ceil(r.exacto)
                    : Math.floor(r.exacto);
            } else {
                gramos = Math.round(r.exacto);
            }

            return {
                nombre: r.nombre,
                gramos,
            };
        });

        setResultados(res);
        setMostrarModalResultado(true);
    };

	const cerrarModal = () => setMostrarModalResultado(false);

	const limpiarFormulario = () => {
		setPesoTotal('');
		setPersonas([
			{ nombre: 'Paula', racion: 0 },
		    { nombre: 'Alfonso', racion: 0 },
		]);
		setResultados([]);
		setMostrarModalResultado(false);
	};

	return (
		<div className="container-fluid">
			<div className="text-center">
				<h2>Calculadora de raciones</h2>
			</div>

			<div className="form-raciones container-fluid">
				<TextField
					label="Peso total cocinado (gr)"
					type="number"
					fullWidth
					value={pesoTotal}
					onChange={(e) => setPesoTotal(e.target.value === '' ? '' : Number(e.target.value))}
					sx={{ mb: 2 }}
					inputProps={{ min: 1 }}
				/>

				<p className="section-label">Ración deseada por persona</p>

				{personas.map((p, i) => (
					<div className="persona-card" key={i}>
						<div className="row align-items-center g-2 persona-row">
                            <div className="col-5">
                                <TextField
                                    label="Nombre"
                                    value={p.nombre}
                                    onChange={(e) => actualizarPersona(i, 'nombre', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={{ mb: 0 }}
                                />
                            </div>

                            <div className="col-5">
                                <TextField
                                    label="Ración (gr)"
                                    type="number"
                                    value={p.racion || ''}
                                    onChange={(e) => actualizarPersona(i, 'racion', Number(e.target.value))}
                                    size="small"
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                    sx={{ mb: 0 }}
                                />
                            </div>

                            <div className="col-2 text-center">
                                {personas.length > 1 && (
                                    <IconButton
                                        onClick={() => eliminarPersona(i)}
                                        size="small"
                                        className="persona-delete-inline"
                                    >
                                        ✕
                                    </IconButton>
                                )}
                            </div>
                        </div>
					</div>
				))}

                <button className="add-persona-link" onClick={agregarPersona}>
					+ Añadir persona
				</button>

				<div className="botones-row">
					<button className="reset-button" onClick={limpiarFormulario}>
						Limpiar
					</button>
					<button className="main-button" onClick={calcular}>
						Calcular
					</button>
				</div>
			</div>

			{mostrarModalResultado && (
				<Modal onClose={cerrarModal}>
					<div className="resultado-content">
						<div className="resultado-header">
							<h2>Raciones calculadas</h2>
						</div>

						<div className="resultado-body">
							{resultados.map((r, i) => (
								<div className="grupo-card" key={i}>
									<h4>{r.nombre}</h4>
									<p className="gramos-result">{r.gramos} g</p>
								</div>
							))}
						</div>

						<p className="resultado-footer d-flex justify-content-between">
                            <span>Total cocinado: {pesoTotal} g</span>
                            <span>Raciones: {resultados.length}</span>
                        </p>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default CalculadoraRaciones;
